import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Inbox, Send, Archive, Trash2, AlertTriangle, FileText,
  ChevronRight, ChevronDown, Settings2, Tag, Bookmark, X, Plus, Check,
} from "lucide-react";

const FOLDERS = [
  { key: "inbox", label: "Inbox", icon: Inbox },
  { key: "sent", label: "Sent", icon: Send },
  { key: "drafts", label: "Drafts", icon: FileText },
  { key: "spam", label: "Spam", icon: AlertTriangle },
  { key: "trash", label: "Trash", icon: Trash2 },
  { key: "archive", label: "Archive", icon: Archive },
];

const PERSIST_KEY = "inbox_sidebar_expanded";

function loadExpanded() {
  try {
    const val = localStorage.getItem(PERSIST_KEY);
    return val ? JSON.parse(val) : {};
  } catch { return {}; }
}

function saveExpanded(state) {
  try { localStorage.setItem(PERSIST_KEY, JSON.stringify(state)); } catch {}
}

/* Filter dropdown for categories or labels */
function FilterDropdown({ items, selectedIds, onToggle, icon: Icon, label, showColorDot = true }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex items-center gap-1 text-[11px] text-[#888888] hover:text-[#555555] transition-colors py-1"
          data-testid={`filter-add-${label.toLowerCase()}`}
        >
          <Plus size={11} className="shrink-0" />
          <span>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="right" className="w-48 p-0" data-testid={`filter-dropdown-${label.toLowerCase()}`}>
        <div className="py-1">
          {items.length === 0 && (
            <p className="text-xs text-zinc-400 text-center py-3">No {label.toLowerCase()} yet</p>
          )}
          {items.map((item) => {
            const isSelected = selectedIds.includes(item.id);
            return (
              <button
                key={item.id}
                onClick={() => onToggle(item.id)}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[#F5F5F5] text-left"
                data-testid={`filter-option-${item.id}`}
              >
                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                  isSelected ? "bg-zinc-900 border-zinc-900" : "border-[#7A7A7A]"
                }`}>
                  {isSelected && <Check size={9} className="text-white" strokeWidth={3} />}
                </div>
                {showColorDot && <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} />}
                <span className="text-xs text-[#555555] truncate">{item.name}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function InboxSidebar({
  aliases, totalAliases, selectedAliasId, selectedFolder, onSelect, totalUnread,
  categories, labels, activeFilters, onFilterChange,
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(() => {
    const saved = loadExpanded();
    if (Object.keys(saved).length === 0 && aliases.length > 0) {
      return { [aliases[0].alias_id]: true };
    }
    return saved;
  });
  const [folderCounts, setFolderCounts] = useState({});

  useEffect(() => { saveExpanded(expanded); }, [expanded]);

  useEffect(() => {
    if (selectedAliasId && !expanded[selectedAliasId]) {
      setExpanded((prev) => ({ ...prev, [selectedAliasId]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAliasId]);

  useEffect(() => {
    if (!selectedAliasId && aliases.length > 0) {
      onSelect(aliases[0].alias_id, "inbox");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aliases.length]);

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/emails/folder-counts");
      setFolderCounts(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchCounts(); }, [fetchCounts, selectedFolder]);
  useEffect(() => {
    if (aliases.length === 0) return;
    const id = setInterval(fetchCounts, 10000);
    return () => clearInterval(id);
  }, [fetchCounts, aliases.length]);

  const toggleAlias = (aliasId) => {
    setExpanded((prev) => ({ ...prev, [aliasId]: !prev[aliasId] }));
  };

  const activeCount = aliases.length;
  const totalCount = totalAliases || aliases.length;

  // Resolve filter items for display
  const activeCatFilters = (activeFilters?.categoryIds || [])
    .map(id => categories.find(c => c.category_id === id))
    .filter(Boolean);
  const activeLblFilters = (activeFilters?.labelIds || [])
    .map(id => labels.find(l => l.label_id === id))
    .filter(Boolean);
  const hasActiveFilters = activeCatFilters.length > 0 || activeLblFilters.length > 0;

  const catItems = categories.map(c => ({ id: c.category_id, name: c.name, color: c.color }));
  const lblItems = labels.map(l => ({ id: l.label_id, name: l.name, color: l.color }));

  const toggleCatFilter = (catId) => {
    const current = activeFilters?.categoryIds || [];
    const next = current.includes(catId)
      ? current.filter(id => id !== catId)
      : [...current, catId];
    onFilterChange({ ...activeFilters, categoryIds: next });
  };

  const toggleLblFilter = (lblId) => {
    const current = activeFilters?.labelIds || [];
    const next = current.includes(lblId)
      ? current.filter(id => id !== lblId)
      : [...current, lblId];
    onFilterChange({ ...activeFilters, labelIds: next });
  };

  const removeCatFilter = (catId) => {
    onFilterChange({
      ...activeFilters,
      categoryIds: (activeFilters?.categoryIds || []).filter(id => id !== catId),
    });
  };

  const removeLblFilter = (lblId) => {
    onFilterChange({
      ...activeFilters,
      labelIds: (activeFilters?.labelIds || []).filter(id => id !== lblId),
    });
  };

  return (
    <div className="w-full h-full flex flex-col bg-white border-r border-[#E5E5E5]" data-testid="inbox-sidebar">
      <ScrollArea className="flex-1">
        <div className="py-2">
          {/* Active alias status */}
          <div className="px-4 py-2.5 border-b border-[#E5E5E5] mb-1" data-testid="sidebar-alias-status">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
              <span className="text-[11px] text-[#555555] font-medium">Active aliases</span>
              <span className="text-[11px] text-[#888888]">shown — {activeCount}/{totalCount}</span>
            </div>
            <button
              onClick={() => navigate("/aliases")}
              className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[#888888] hover:text-[#555555] transition-colors font-medium"
              data-testid="sidebar-manage-aliases"
            >
              <Settings2 size={12} />
              <span>Manage</span>
            </button>
          </div>

          {/* Alias Sections */}
          {aliases.map((alias) => {
            const isOpen = !!expanded[alias.alias_id];
            const counts = folderCounts[alias.alias_id] || {};
            const inboxUnread = counts.inbox?.unread || 0;
            const isSelected = selectedAliasId === alias.alias_id;

            return (
              <div key={alias.alias_id} data-testid={`alias-section-${alias.alias_id}`}>
                {/* Alias Header */}
                <button
                  onClick={() => toggleAlias(alias.alias_id)}
                  className="w-full flex items-center gap-2 px-4 py-2 mt-0.5 text-left hover:bg-[#F5F5F5] transition-colors group"
                  data-testid={`alias-toggle-${alias.alias_id}`}
                >
                  {isOpen
                    ? <ChevronDown size={14} className="text-[#888888] shrink-0" />
                    : <ChevronRight size={14} className="text-[#888888] shrink-0" />
                  }
                  <div className="flex-1 min-w-0" title={alias.alias_address}>
                    <p className="text-sm font-semibold text-[#111111] truncate">{alias.label || alias.alias_address.split("@")[0]}</p>
                  </div>
                  {inboxUnread > 0 && (
                    <span className="text-[10px] font-mono font-medium text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded shrink-0">{inboxUnread}</span>
                  )}
                </button>

                {/* Folder List */}
                {isOpen && (
                  <div className="ml-4 border-l border-[#E5E5E5]" data-testid={`alias-folders-${alias.alias_id}`}>
                    {FOLDERS.map((f) => {
                      const isActive = isSelected && selectedFolder === f.key;
                      const FIcon = f.icon;
                      const inboxUnread = f.key === "inbox" ? (counts.inbox?.unread || 0) : 0;
                      let countDisplay = null;
                      if (f.key === "inbox" && counts.inbox) {
                        const t = counts.inbox.total || 0;
                        if (t > 0) countDisplay = String(t);
                      } else if (f.key === "sent" && counts.sent) {
                        countDisplay = String(counts.sent);
                      } else if (f.key === "archive" && counts.archive) {
                        countDisplay = String(counts.archive);
                      } else if (f.key === "trash" && counts.trash) {
                        countDisplay = String(counts.trash);
                      } else if (f.key === "spam" && counts.spam) {
                        countDisplay = String(counts.spam);
                      } else if (f.key === "drafts" && counts.drafts) {
                        countDisplay = String(counts.drafts);
                      }

                      return (
                        <button
                          key={f.key}
                          onClick={() => onSelect(alias.alias_id, f.key)}
                          className={`w-full flex items-center gap-2.5 pl-4 pr-3 py-1.5 text-left transition-colors ${
                            isActive
                              ? "bg-[#F5F5F5] text-[#111111] font-medium"
                              : "text-[#555555] hover:bg-[#F5F5F5] hover:text-[#111111]"
                          }`}
                          data-testid={`folder-${alias.alias_id}-${f.key}`}
                        >
                          <FIcon size={16} className={isActive ? "text-[#111111]" : "text-[#888888]"} />
                          <span className="text-[13px] flex-1">
                            {f.label}
                            {f.key === "inbox" && inboxUnread > 0 && (
                              <span className="ml-1.5 text-[11px] font-semibold text-violet-600">({inboxUnread})</span>
                            )}
                          </span>
                          {countDisplay && (
                            <span className={`text-[10px] font-mono shrink-0 ${f.key === "inbox" && (counts.inbox?.unread || 0) > 0 ? "text-violet-600 font-medium" : "text-[#888888]"}`}>
                              {countDisplay}
                            </span>
                          )}
                        </button>
                      );
                    })}

                    {/* ── Filter Section (only for selected alias) ── */}
                    {isSelected && (
                      <div className="pl-4 pr-3 pt-3 pb-2" data-testid="sidebar-filter-section">

                        {!hasActiveFilters ? (
                          <p className="text-[10px] text-[#888888]">No filters applied</p>
                        ) : null}

                        {/* Categories group */}
                        {(activeCatFilters.length > 0 || !hasActiveFilters) && (
                          <div className={hasActiveFilters ? "" : "mt-2.5"}>
                            <p className="text-[9px] uppercase tracking-wide text-[#888888] mb-1.5">Categories</p>
                            {activeCatFilters.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5" data-testid="active-cat-filters">
                                {activeCatFilters.map((cat) => (
                                  <span
                                    key={cat.category_id}
                                    className="inline-flex items-center gap-1 h-[22px] text-[10px] font-medium text-white pl-2 pr-1 rounded"
                                    style={{ backgroundColor: cat.color }}
                                    data-testid={`active-filter-cat-${cat.category_id}`}
                                  >
                                    {cat.name}
                                    <button onClick={() => removeCatFilter(cat.category_id)} className="hover:opacity-70 p-0.5"><X size={9} /></button>
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            <FilterDropdown
                              items={catItems}
                              selectedIds={activeFilters?.categoryIds || []}
                              onToggle={toggleCatFilter}
                              icon={Tag}
                              label="Categories"
                            />
                          </div>
                        )}

                        {/* Labels group — spacing, no divider */}
                        {(activeLblFilters.length > 0 || !hasActiveFilters) && (
                          <div className="mt-3">
                            <p className="text-[9px] uppercase tracking-wide text-[#888888] mb-1.5">Labels</p>
                            {activeLblFilters.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5" data-testid="active-lbl-filters">
                                {activeLblFilters.map((lbl) => (
                                  <span
                                    key={lbl.label_id}
                                    className="inline-flex items-center gap-1 h-[22px] text-[10px] font-normal text-[#555555] bg-[#F5F5F5] border border-[#E5E5E5] pl-2 pr-1 rounded"
                                    data-testid={`active-filter-lbl-${lbl.label_id}`}
                                  >
                                    {lbl.name}
                                    <button onClick={() => removeLblFilter(lbl.label_id)} className="hover:opacity-70 p-0.5"><X size={9} /></button>
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            <FilterDropdown
                              items={lblItems}
                              selectedIds={activeFilters?.labelIds || []}
                              onToggle={toggleLblFilter}
                              icon={Bookmark}
                              label="Labels"
                              showColorDot={false}
                            />
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
